import { AlarmClock, CircleCheck, CircleDashed, Clock, Inbox, type LucideIcon } from "lucide-react";
import type { MailTask, MailTaskFilter, MailTaskStatus } from "./types";

/**
 * One task's status, by the same rule the list uses: overdue when open and due before now, in UTC.
 */
export const taskStatusOf = (task: MailTask, now: Date): Exclude<MailTaskStatus, "none"> => {
  if (task.isCompleted) return "done";
  if (task.dueDateUtc && new Date(task.dueDateUtc).getTime() < now.getTime()) return "overdue";
  return "open";
};

type TaskStatusStyle = {
  label: string;
  /** Read out and shown on hover, as the badge itself is short. */
  description: string;
  icon: LucideIcon;
  /** Matches the task pills: open is amber, done green, with overdue red. */
  className: string;
};

export const TASK_STATUS_STYLES: Record<Exclude<MailTaskStatus, "none">, TaskStatusStyle> = {
  overdue: {
    label: "Overdue",
    description: "A task for this email is overdue",
    icon: AlarmClock,
    className: "border-red-200 bg-red-50 text-red-700",
  },
  open: {
    label: "To do",
    description: "A task for this email is still open",
    icon: Clock,
    className: "border-amber-200 bg-amber-50 text-amber-800",
  },
  done: {
    label: "Done",
    description: "Every task for this email is completed",
    icon: CircleCheck,
    className: "border-emerald-200 bg-emerald-50 text-emerald-700",
  },
};

type TaskFilterOption = {
  value: MailTaskFilter;
  label: string;
  icon: LucideIcon;
  /** What an empty list means with only this filter on. */
  emptyDescription: string;
};

export const TASK_FILTER_OPTIONS: TaskFilterOption[] = [
  { value: "all", label: "All emails", icon: Inbox, emptyDescription: "" },
  {
    value: "open",
    label: "To do",
    icon: Clock,
    emptyDescription: "No emails have open tasks. Everything is dealt with.",
  },
  {
    value: "overdue",
    label: "Overdue",
    icon: AlarmClock,
    emptyDescription: "No emails have overdue tasks.",
  },
  {
    value: "done",
    label: "Done",
    icon: CircleCheck,
    emptyDescription: "No emails have all their tasks completed.",
  },
  {
    value: "noTask",
    label: "No task",
    icon: CircleDashed,
    emptyDescription: "Every email has a task.",
  },
];

export const taskFilterOption = (value: MailTaskFilter): TaskFilterOption =>
  TASK_FILTER_OPTIONS.find(option => option.value === value) ?? TASK_FILTER_OPTIONS[0];
